import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import logo from '../assets/logo.png';

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const getDefaultRedirect = (role) => {
    if (role === 'admin' || role === 'manager' || role === 'instructor') return '/admin';
    if (role === 'student') return '/student';
    return '/';
  };

  const getRedirectPath = (fromPath, role) => {
    const defaultPath = getDefaultRedirect(role);
    if (!fromPath || fromPath === '/' || fromPath === '/login') return defaultPath;
    if (fromPath.startsWith('/admin') && !['admin', 'manager', 'instructor'].includes(role)) return defaultPath;
    if (fromPath.startsWith('/student') && role !== 'student') return defaultPath;
    return fromPath;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    const result = await login(email, password, remember);

    if (result.success) {
      const redirectPath = getRedirectPath(from, result.user?.role);
      navigate(redirectPath, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfo('');
    if (!email) {
      setError('Vul eerst je e-mailadres in om te resetten.');
      return;
    }

    try {
      const result = await authAPI.forgotPassword(email);
      setInfo(result?.message || 'Als dit e-mailadres bekend is, is een resetbericht verstuurd.');
    } catch (forgotError) {
      setError(forgotError.message || 'Wachtwoord reset aanvragen mislukt.');
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />
      
      <section style={styles.loginSection}>
        <div style={styles.loginCard}>
          <div style={styles.logoContainer}>
            <img src={logo} alt="NLBA" style={styles.logoImage} />
            <h1 style={styles.title}>{t('auth.loginTitle')}</h1>
          </div>
          
          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {info && (
            <div style={styles.info}>
              {info}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{t('auth.password')}</label>
              <div style={styles.passwordInputWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...styles.input, ...styles.passwordInput }}
                  required
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((prev) => !prev)}
                  style={styles.passwordToggleBtn}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C7 20 2.73 16.89 1 12c.88-2.5 2.54-4.61 4.73-6.09" />
                      <path d="M9.9 4.24A11.26 11.26 0 0 1 12 4c5 0 9.27 3.11 11 8a10.94 10.94 0 0 1-1.67 2.93" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div style={styles.optionsRow}>
              <div style={styles.checkboxGroup}>
                <input
                  id="remember"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  style={styles.checkbox}
                />
                <label htmlFor="remember" style={styles.checkboxLabel}>
                  {t('auth.rememberMe')}
                </label>
              </div>

              <button
                type="button"
                style={styles.forgotButton}
                onClick={handleForgotPassword}
              >
                Wachtwoord vergeten?
              </button>
            </div>

            <button 
              type="submit" 
              style={loading ? styles.buttonLoading : styles.button}
              disabled={loading}
            >
              {loading ? t('auth.loggingIn') : t('auth.loginButton')}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  },
  loginSection: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#f5f5f5'
  },
  loginCard: {
    backgroundColor: '#ffffff',
    padding: '2.5rem',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '400px'
  },
  title: {
    fontSize: '2rem',
    color: '#333',
    marginBottom: '1.5rem',
    textAlign: 'center'
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  logoImage: {
    width: '200px',
    height: '100px',
    marginBottom: '0.75rem'
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    textAlign: 'center'
  },
  info: {
    backgroundColor: '#ecfdf5',
    color: '#047857',
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    textAlign: 'center'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  optionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#333'
  },
  input: {
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem'
  },
  passwordInputWrap: {
    position: 'relative'
  },
  passwordInput: {
    width: '100%',
    paddingRight: '2.7rem'
  },
  passwordToggleBtn: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    border: 'none',
    background: 'transparent',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: '6px'
  },
  button: {
    backgroundColor: 'var(--primary)',
    color: 'var(--white)',
    padding: '0.75rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '0.5rem'
  },
  buttonLoading: {
    backgroundColor: 'var(--primary-light)',
    color: 'var(--white)',
    padding: '0.75rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'not-allowed',
    marginTop: '0.5rem'
  },
  demoInfo: {
    marginTop: '2rem',
    padding: '1rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    fontSize: '0.875rem',
    color: '#666'
  },
  demoTitle: {
    fontWeight: 'bold',
    marginBottom: '0.5rem'
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem'
  },
  checkbox: {
    width: '16px',
    height: '16px'
  },
  checkboxLabel: {
    fontSize: '0.875rem',
    color: '#333'
  },
  forgotButton: {
    border: 'none',
    background: 'transparent',
    color: 'var(--primary)',
    textAlign: 'right',
    cursor: 'pointer',
    padding: 0,
    marginTop: 0,
    fontSize: '0.875rem',
    fontWeight: '600',
    marginLeft: 'auto'
  }
};

export default Login;
